import { useRef, useState } from 'react';
import { toPng } from 'html-to-image';
import { Check, Copy, Download, Loader2, Star } from 'lucide-react';
import type { getStarsReport } from '@generated/server/stars';

type Report = Awaited<ReturnType<typeof getStarsReport>>;
const head = 'font-[family-name:var(--font-heading)]';
const PER = 5;

function postText(r: Report, final: boolean) {
  const lines = [
    final ? '🌟 RED HOOK STARS — THE WINNERS ARE IN! 🌟' : '🌟 RED HOOK STARS — WHO\'S LEADING? 🌟',
    final ? `Thank you to all ${r.total} neighbors who voted in our Community Appreciation Awards.` : `${r.total} neighbors have voted so far in our Community Appreciation Awards. Voting closes November 10 — there's still time to change the standings!`,
    '',
    ...r.categories.filter((c) => c.top.length).map((c) => `${c.label}: ${c.top[0]?.name}${c.top.length > 1 ? ` (then ${c.top.slice(1).map((t) => t.name).join(', ')})` : ''}`),
    '',
    final ? 'Congratulations to all our Red Hook Stars! — Red Hook Star-Revue' : 'Cast your vote before November 10! — Red Hook Star-Revue',
  ];
  return lines.join('\n');
}

export function StarsSocial({ r, final }: { r: Report; final: boolean }) {
  const cats = r.categories.filter((c) => c.top.length);
  const pages = Array.from({ length: Math.ceil(cats.length / PER) }, (_, i) => cats.slice(i * PER, i * PER + PER));
  const refs = useRef<(HTMLDivElement | null)[]>([]);
  const [busy, setBusy] = useState<number | 'all' | null>(null);
  const [copied, setCopied] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function save(i: number) {
    const node = refs.current[i]; if (!node) return;
    const url = await toPng(node, { pixelRatio: 2, cacheBust: true, backgroundColor: '#ffffff' });
    const a = document.createElement('a'); a.href = url; a.download = `red-hook-stars-${i + 1}-of-${pages.length + 1}.png`; a.click();
  }
  async function run(which: number | 'all') {
    setBusy(which); setError(null);
    try { if (which === 'all') { for (let i = 0; i <= pages.length; i++) { await save(i); await new Promise((res) => setTimeout(res, 400)); } } else await save(which); }
    catch (e) { console.error(e); setError('The image could not be saved. Please try again.'); }
    finally { setBusy(null); }
  }
  async function copy() {
    try { await navigator.clipboard.writeText(postText(r, final)); setCopied(true); setTimeout(() => setCopied(false), 2000); }
    catch (e) { console.error(e); setError('Copy was blocked by the browser — select the text below and copy it by hand.'); }
  }
  const btn = 'inline-flex min-h-11 items-center gap-2 border border-foreground px-4 text-sm font-semibold disabled:opacity-50';
  const frame = 'relative flex h-[675px] w-[540px] shrink-0 flex-col overflow-hidden bg-[hsl(var(--paper))] text-foreground';
  const title = final ? 'Winner' : 'Leader';

  return (
    <section className="mt-12 print:hidden">
      <h2 className={`${head} text-2xl font-semibold`}>Post it on Facebook</h2>
      <p className="mt-1 text-sm text-muted-foreground">Download the images (post them together as one album) and paste the caption with them.</p>
      <div className="mt-4 flex flex-wrap gap-2">
        <button className={btn} disabled={busy !== null} onClick={() => void run('all')}>{busy === 'all' ? <Loader2 className="h-4 w-4 animate-spin" /> : <Download className="h-4 w-4" />}Download all {pages.length + 1} images</button>
        <button className={btn} onClick={() => void copy()}>{copied ? <Check className="h-4 w-4" /> : <Copy className="h-4 w-4" />}{copied ? 'Caption copied' : 'Copy caption'}</button>
      </div>
      {error && <p role="alert" className="mt-2 text-sm text-destructive">{error}</p>}

      <div className="mt-5 flex gap-4 overflow-x-auto pb-4">
        <div className="shrink-0">
          <div ref={(el) => { refs.current[0] = el; }} className={`${frame} items-center justify-center border-[14px] border-primary p-10 text-center`}>
            <div className="text-xs font-semibold uppercase tracking-[0.25em] text-primary">Red Hook Star-Revue presents</div>
            <Star className="mt-6 h-16 w-16 fill-[hsl(var(--brand-yellow))] text-foreground" />
            <h3 className={`${head} mt-4 text-6xl font-bold leading-none`}>Red Hook Stars</h3>
            <p className={`${head} mt-3 text-2xl`}>Community Appreciation Awards</p>
            <div className="mt-8 bg-foreground px-5 py-2 text-lg font-semibold uppercase tracking-wide text-[hsl(var(--paper))]">{final ? 'The winners' : 'Current leaders'}</div>
            <p className="mt-6 text-lg">{r.total} neighbors voted</p>
            {!final && <p className="mt-2 text-base font-semibold text-primary">Voting closes November 10</p>}
          </div>
          <button className="mt-2 text-xs underline" disabled={busy !== null} onClick={() => void run(0)}>Download this image</button>
        </div>
        {pages.map((pg, pi) => (
          <div key={pg[0]?.label ?? 'page'} className="shrink-0">
            <div ref={(el) => { refs.current[pi + 1] = el; }} className={`${frame} p-8`}>
              <div className="flex items-baseline justify-between border-b-4 border-double border-foreground pb-2">
                <span className={`${head} text-2xl font-bold`}>Red Hook Stars</span>
                <span className="text-xs font-semibold uppercase tracking-wide text-primary">{final ? 'Winners' : 'Standings'} · {pi + 1}/{pages.length}</span>
              </div>
              <div className="mt-3 flex flex-1 flex-col justify-around">
                {pg.map((c) => (
                  <div key={c.label}>
                    <div className="text-[11px] font-semibold uppercase tracking-[0.15em] text-muted-foreground">{c.label}</div>
                    <div className={`${head} flex items-center gap-2 text-2xl font-bold leading-tight text-primary`}><Star className="h-5 w-5 shrink-0 fill-[hsl(var(--brand-yellow))] text-foreground" />{c.top[0]?.name}<span className="ml-auto text-[10px] font-semibold uppercase tracking-wide text-foreground">{title}</span></div>
                    {c.top.length > 1 && <div className="text-sm">{c.top.slice(1).map((t, i) => `${i === 0 ? '2nd' : '3rd'}: ${t.name}`).join('  ·  ')}</div>}
                  </div>
                ))}
              </div>
              <div className="mt-2 border-t border-foreground pt-2 text-center text-xs">{final ? 'Congratulations to all our Red Hook Stars!' : 'Voting closes November 10'} · star-revue.com</div>
            </div>
            <button className="mt-2 text-xs underline" disabled={busy !== null} onClick={() => void run(pi + 1)}>Download this image</button>
          </div>
        ))}
      </div>

      <label className="mt-2 block text-sm font-semibold" htmlFor="fb-caption">Caption</label>
      <textarea id="fb-caption" readOnly value={postText(r, final)} className="mt-1 h-48 w-full border border-border bg-background p-3 text-sm" />
    </section>
  );
}
